import { useEffect, useMemo, useRef, useState } from "react";
import SubscriberStatusCell from "./admin/SubscriberStatusCell";

const PAGE_SIZE = 12;

function formatDate(value) {
  try {
    return new Intl.DateTimeFormat("hu-HU", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function getEmailInitial(email) {
  return String(email || "?").charAt(0).toUpperCase();
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function computeStats(subscribers) {
  const now = new Date();
  const todayStart = startOfDay(now);
  const weekAgo = new Date(now);
  weekAgo.setDate(weekAgo.getDate() - 7);

  let todayCount = 0;
  let weekCount = 0;
  let visitedCount = 0;
  let noShowCount = 0;

  for (const entry of subscribers) {
    const date = new Date(entry.subscribedAt);
    if (date >= todayStart) todayCount += 1;
    if (date >= weekAgo) weekCount += 1;
    if (entry.visitStatus === "visited") visitedCount += 1;
    if (entry.visitStatus === "no_show") noShowCount += 1;
  }

  return {
    total: subscribers.length,
    todayCount,
    weekCount,
    visitedCount,
    noShowCount,
  };
}

function exportCsv(rows) {
  const header = "customerNumber,email,lang,subscribedAt,visitStatus";
  const lines = rows.map(
    (row) =>
      `"${row.customerNumber ?? ""}","${row.email}","${row.lang}","${new Date(row.subscribedAt).toISOString()}","${row.visitStatus || "unknown"}"`,
  );
  const blob = new Blob([[header, ...lines].join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "mizu-feliratkozok.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function IconSearch() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
    </svg>
  );
}

function IconRefresh() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </svg>
  );
}

function IconDownload() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
    </svg>
  );
}

function IconEye() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    </svg>
  );
}

function IconMore() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  );
}

function IconTrend() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z" />
    </svg>
  );
}

function AdminDashboard({
  subscribers,
  loading,
  refreshing,
  onRefresh,
  onDeleteSubscriber,
  onUpdateSubscriberStatus,
  onImportSubscribers,
  onSendOpeningEmail,
  lastRefreshedAt,
}) {
  const [search, setSearch] = useState("");
  const [langFilter, setLangFilter] = useState("all");
  const [visitFilter, setVisitFilter] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importLang, setImportLang] = useState("hu");
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState("");
  const [openMenuEmail, setOpenMenuEmail] = useState(null);
  const [deletingEmail, setDeletingEmail] = useState(null);
  const [sendingOpeningEmail, setSendingOpeningEmail] = useState(null);
  const [savingStatusEmail, setSavingStatusEmail] = useState(null);
  const filterRef = useRef(null);

  const stats = useMemo(() => computeStats(subscribers), [subscribers]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return subscribers.filter((entry) => {
      if (langFilter !== "all" && entry.lang !== langFilter) return false;
      const visitStatus = entry.visitStatus || "unknown";
      if (visitFilter !== "all" && visitStatus !== visitFilter) return false;
      if (query && !entry.email.toLowerCase().includes(query)) {
        const numberMatch = String(entry.customerNumber ?? "").includes(query);
        if (!numberMatch) return false;
      }
      return true;
    });
  }, [subscribers, search, langFilter, visitFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, langFilter, visitFilter]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFiltersOpen(false);
      }
      if (!event.target.closest(".admin-dash-row-menu")) {
        setOpenMenuEmail(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function copyEmail(email) {
    try {
      await navigator.clipboard.writeText(email);
      setToast("Másolva");
    } catch {
      setToast("Nem sikerült másolni");
    }
    setOpenMenuEmail(null);
  }

  async function handleDelete(email) {
    const confirmed = window.confirm(
      `Biztosan törlöd ezt a feliratkozót?\n\n${email}`,
    );
    if (!confirmed) return;

    setDeletingEmail(email);
    setOpenMenuEmail(null);

    try {
      await onDeleteSubscriber(email);
      setToast("Törölve");
    } catch {
      setToast("Nem sikerült törölni");
    } finally {
      setDeletingEmail(null);
    }
  }

  async function handleSendOpeningEmail(entry) {
    if (!onSendOpeningEmail) {
      setToast("A küldés nem érhető el");
      return;
    }

    const numberLabel = entry.customerNumber ? ` (Szám: ${entry.customerNumber})` : "";
    const confirmed = window.confirm(
      `Nyitás bejelentése küldése ide: ${entry.email}?${numberLabel}`,
    );
    if (!confirmed) return;

    setSendingOpeningEmail(entry.email);
    setOpenMenuEmail(null);

    try {
      const result = await onSendOpeningEmail(entry.email);
      setToast(`Elküldve (szám: ${result.customerNumber})`);
    } catch {
      setToast("Nem sikerült elküldeni");
    } finally {
      setSendingOpeningEmail(null);
    }
  }

  async function handleStatusUpdate(email, updates) {
    setSavingStatusEmail(email);

    try {
      await onUpdateSubscriberStatus(email, updates);
    } catch {
      setToast("Státusz mentése sikertelen");
    } finally {
      setSavingStatusEmail(null);
    }
  }

  function handleExport() {
    exportCsv(filtered);
  }

  async function handleImport() {
    if (!importText.trim()) {
      setImportMessage("Írj be legalább egy e-mail címet.");
      return;
    }

    setImporting(true);
    setImportMessage("");

    try {
      const result = await onImportSubscribers({
        text: importText,
        defaultLang: importLang,
      });
      setImportText("");
      setImportOpen(false);
      setToast(
        `${result.added} hozzáadva, ${result.existing} már létezett${result.invalid ? `, ${result.invalid} érvénytelen` : ""}`,
      );
    } catch {
      setImportMessage("Az import sikertelen.");
    } finally {
      setImporting(false);
    }
  }

  return (
    <>
      <header className="admin-dash-header">
          <div>
            <h1 className="admin-dash-title">Feliratkozók</h1>
            <p className="admin-dash-subtitle">
              Összes feliratkozó megtekintése és kezelése.
            </p>
            {lastRefreshedAt && (
              <p className="admin-dash-refreshed">
                Utolsó frissítés: {formatDate(lastRefreshedAt)}
              </p>
            )}
          </div>
        </header>

        <div className="admin-dash-toolbar">
          <label className="admin-dash-search">
            <IconSearch />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Keresés e-mail címre vagy számra…"
            />
          </label>

          <div className="admin-dash-toolbar-actions" ref={filterRef}>
            <button
              type="button"
              className={`admin-dash-btn admin-dash-btn-outline admin-dash-btn-icon${refreshing ? " is-spinning" : ""}`}
              onClick={onRefresh}
              disabled={refreshing || loading}
              aria-label="Frissítés"
              title="Frissítés"
            >
              <IconRefresh />
            </button>

            <button
              type="button"
              className={`admin-dash-btn admin-dash-btn-outline${filtersOpen ? " is-open" : ""}`}
              onClick={() => setFiltersOpen((open) => !open)}
            >
              <IconFilter />
              Szűrők
            </button>

            {filtersOpen && (
              <div className="admin-dash-filters">
                <p className="admin-dash-filters-label">Nyelv</p>
                <div className="admin-dash-filter-options">
                  {[
                    { value: "all", label: "Összes" },
                    { value: "hu", label: "HU" },
                    { value: "en", label: "EN" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`admin-dash-filter-option${langFilter === option.value ? " is-active" : ""}`}
                      onClick={() => setLangFilter(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <p className="admin-dash-filters-label">Látogatás</p>
                <div className="admin-dash-filter-options">
                  {[
                    { value: "all", label: "Összes" },
                    { value: "unknown", label: "Nincs" },
                    { value: "visited", label: "Megvolt" },
                    { value: "no_show", label: "Nem jött" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`admin-dash-filter-option${visitFilter === option.value ? " is-active" : ""}`}
                      onClick={() => setVisitFilter(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              className={`admin-dash-btn admin-dash-btn-outline${importOpen ? " is-open" : ""}`}
              onClick={() => {
                setImportOpen((open) => !open);
                setImportMessage("");
              }}
            >
              Hozzáadás
            </button>

            <button
              type="button"
              className="admin-dash-btn admin-dash-btn-primary"
              onClick={handleExport}
              disabled={filtered.length === 0}
            >
              <IconDownload />
              Exportálás
            </button>
          </div>
        </div>

        {importOpen && (
          <section className="admin-import-card">
            <h2 className="admin-import-title">Feliratkozók hozzáadása</h2>
            <p className="admin-import-help">
              Egy e-mail cím soronként. Opcionális nyelv:{" "}
              <code>email@example.com,en</code> vagy <code>email@example.com en</code>
            </p>
            <label className="admin-email-field">
              <span>Alapértelmezett nyelv</span>
              <select
                value={importLang}
                onChange={(event) => setImportLang(event.target.value)}
              >
                <option value="hu">Magyar</option>
                <option value="en">English</option>
              </select>
            </label>
            <label className="admin-email-field">
              <span>E-mail címek</span>
              <textarea
                className="admin-import-textarea"
                value={importText}
                onChange={(event) => setImportText(event.target.value)}
                placeholder={"anna@example.com\njohn@example.com,en"}
                rows={8}
              />
            </label>
            <div className="admin-import-actions">
              <button
                type="button"
                className="admin-dash-btn admin-dash-btn-primary"
                onClick={handleImport}
                disabled={importing}
              >
                {importing ? "Importálás…" : "Importálás"}
              </button>
              <button
                type="button"
                className="admin-dash-btn admin-dash-btn-outline"
                onClick={() => {
                  setImportOpen(false);
                  setImportMessage("");
                }}
                disabled={importing}
              >
                Mégse
              </button>
            </div>
            {importMessage && (
              <p className="admin-email-error" role="alert">
                {importMessage}
              </p>
            )}
          </section>
        )}

        <div className="admin-dash-stats">
          <article className="admin-dash-stat">
            <p className="admin-dash-stat-label">Összes feliratkozó</p>
            <p className="admin-dash-stat-value">{stats.total}</p>
            <p className="admin-dash-stat-trend">
              <IconTrend />
              +{stats.weekCount} az elmúlt 7 napban
            </p>
          </article>

          <article className="admin-dash-stat">
            <p className="admin-dash-stat-label">Új ma</p>
            <p className="admin-dash-stat-value">{stats.todayCount}</p>
            <p className="admin-dash-stat-trend admin-dash-stat-trend-muted">
              Ma regisztrált
            </p>
          </article>

          <article className="admin-dash-stat">
            <p className="admin-dash-stat-label">Megvolt</p>
            <p className="admin-dash-stat-value">{stats.visitedCount}</p>
            <p className="admin-dash-stat-trend admin-dash-stat-trend-muted">
              {stats.noShowCount} nem jött
            </p>
          </article>
        </div>

        <section className="admin-dash-table-card">
          {loading ? (
            <p className="admin-dash-empty">Betöltés…</p>
          ) : filtered.length === 0 ? (
            <p className="admin-dash-empty">Nincs találat.</p>
          ) : (
            <>
              <div className="admin-dash-table-wrap">
                <table className="admin-dash-table">
                  <thead>
                    <tr>
                      <th>Szám</th>
                      <th>E-mail</th>
                      <th>Nyelv</th>
                      <th>Feliratkozás</th>
                      <th>Státusz</th>
                      <th>Műveletek</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((entry) => (
                      <tr key={entry.email}>
                        <td>
                          <span className="admin-dash-customer-number">
                            {entry.customerNumber ?? "—"}
                          </span>
                        </td>
                        <td>
                          <div className="admin-dash-email-cell">
                            <span className="admin-dash-email-avatar">
                              {getEmailInitial(entry.email)}
                            </span>
                            <span>{entry.email}</span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`admin-dash-lang-badge admin-dash-lang-badge-${entry.lang}`}
                          >
                            {entry.lang === "en" ? "EN" : "HU"}
                          </span>
                        </td>
                        <td>{formatDate(entry.subscribedAt)}</td>
                        <td>
                          <SubscriberStatusCell
                            entry={entry}
                            saving={savingStatusEmail === entry.email}
                            onUpdate={handleStatusUpdate}
                          />
                        </td>
                        <td>
                          <div className="admin-dash-row-actions">
                            <button
                              type="button"
                              className="admin-dash-send-btn"
                              disabled={sendingOpeningEmail === entry.email}
                              onClick={() => handleSendOpeningEmail(entry)}
                            >
                              {sendingOpeningEmail === entry.email
                                ? "Küldés…"
                                : "Nyitás e-mail"}
                            </button>
                            <button
                              type="button"
                              className="admin-dash-icon-btn"
                              aria-label="E-mail másolása"
                              onClick={() => copyEmail(entry.email)}
                            >
                              <IconEye />
                            </button>
                            <div className="admin-dash-row-menu">
                              <button
                                type="button"
                                className="admin-dash-icon-btn"
                                aria-label="További műveletek"
                                aria-expanded={openMenuEmail === entry.email}
                                onClick={() =>
                                  setOpenMenuEmail((current) =>
                                    current === entry.email ? null : entry.email,
                                  )
                                }
                              >
                                <IconMore />
                              </button>
                              {openMenuEmail === entry.email && (
                                <div className="admin-dash-dropdown">
                                  <button
                                    type="button"
                                    className="admin-dash-dropdown-send"
                                    disabled={sendingOpeningEmail === entry.email}
                                    onClick={() => handleSendOpeningEmail(entry)}
                                  >
                                    {sendingOpeningEmail === entry.email
                                      ? "Küldés…"
                                      : "Nyitás e-mail küldése"}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => copyEmail(entry.email)}
                                  >
                                    E-mail másolása
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-dash-dropdown-danger"
                                    disabled={deletingEmail === entry.email}
                                    onClick={() => handleDelete(entry.email)}
                                  >
                                    {deletingEmail === entry.email ? "Törlés…" : "Törlés"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <footer className="admin-dash-table-foot">
                <p>
                  {filtered.length === 0
                    ? "0 feliratkozó"
                    : `${pageStart + 1}–${Math.min(pageStart + PAGE_SIZE, filtered.length)} / ${filtered.length} feliratkozó`}
                </p>
                <div className="admin-dash-pagination">
                  <button
                    type="button"
                    className="admin-dash-page-btn"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    aria-label="Előző oldal"
                  >
                    ‹
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        className={`admin-dash-page-btn${pageNumber === currentPage ? " is-active" : ""}`}
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    ),
                  )}
                  <button
                    type="button"
                    className="admin-dash-page-btn"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    aria-label="Következő oldal"
                  >
                    ›
                  </button>
                </div>
              </footer>
            </>
          )}
        </section>

      {toast && (
        <div className="admin-dash-toast" role="status">
          {toast}
        </div>
      )}
    </>
  );
}

export default AdminDashboard;
